import * as React from "react";
import { AlertColor, Grid } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import {
    useGetReferenceDataQuery,
    useToggleAdminLocationActiveStatusMutation,
} from "../../../services/cclTokenedPayrollApi";
import { PayrollAdminLocation } from "../../../services/types/payrollEventTypes";
import AddEditAdminLocationDrawer from "./addEditAdminLocationDrawer";

interface AdminLocationPanelProps {}

const getDateFromTimestamp = (date: string | undefined): string =>
    date ? new Date(date).toLocaleDateString() : "";

const toBoolean = (value: unknown): boolean =>
    value === true || String(value ?? "").trim().toLowerCase() === "true";

const normalizeSelectionIds = (selection: any): Array<string | number> => {
    if (Array.isArray(selection)) {
        return selection;
    }

    if (selection?.ids instanceof Set) {
        return Array.from(selection.ids) as Array<string | number>;
    }

    return Array.isArray(selection?.ids) ? selection.ids : [];
};

const AdminLocationPanel: React.FC<AdminLocationPanelProps> = () => {
    const { data: referenceData, isLoading: loading } = useGetReferenceDataQuery();
    const [toggleAdminLocationActiveStatus] = useToggleAdminLocationActiveStatusMutation();
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [drawerMode, setDrawerMode] = React.useState<"add" | "edit">("add");
    const [selectedRows, setSelectedRows] = React.useState<Array<string | number>>([]);
    const [selectedLocation, setSelectedLocation] = React.useState<PayrollAdminLocation | null>(null);
    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const { logEvent } = useLogAccessEvent();

    const rows = React.useMemo<PayrollAdminLocation[]>(() => {
        if (!Array.isArray(referenceData?.locations)) {
            return [];
        }

        return referenceData.locations
            .map((location: PayrollAdminLocation) => ({
                ...location,
                active: toBoolean(location.active),
            }))
            .sort((a: PayrollAdminLocation, b: PayrollAdminLocation) =>
                String(a.locCode ?? "").localeCompare(String(b.locCode ?? ""))
            );
    }, [referenceData]);
    const isExactlyOneSelected = selectedRows.length === 1;
    const currentSelectedLocation = React.useMemo(
        () => isExactlyOneSelected
            ? rows.find(
                (location) => String(location.locCode) === String(selectedRows[0])
            ) ?? null
            : null,
        [isExactlyOneSelected, rows, selectedRows]
    );

    const columns: GridColDef[] = [
        { field: "locCode", headerName: "Code", width: 200 },
        { field: "locDesc", headerName: "Description", width: 300, flex: 1 },
        {
            field: "active",
            headerName: "Enabled",
            type: "boolean",
            width: 120,
            renderCell: (params) => params.value ? "Yes" : "No",
        },
        { field: "createdBy", headerName: "Created By", width: 200 },
        {
            field: "created",
            headerName: "Created",
            width: 140,
            valueGetter: (params) => getDateFromTimestamp(params.row.created),
        },
        { field: "modifiedBy", headerName: "Modified By", width: 200 },
        {
            field: "modified",
            headerName: "Modified",
            width: 140,
            valueGetter: (params) => getDateFromTimestamp(params.row.modified),
        },
    ];

    const showResult = (message: string, severity: AlertColor) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setShowSnackbar(true);
    };

    const handleToolbarButtonClick = (buttonId: string) => {
        if (buttonId === "add-location-button") {
            setDrawerMode("add");
            setSelectedLocation(null);
            setDrawerOpen(true);
            return;
        }

        if (!currentSelectedLocation) {
            return;
        }

        if (buttonId === "edit-location-button") {
            setDrawerMode("edit");
            setSelectedLocation(currentSelectedLocation);
            setDrawerOpen(true);
            return;
        }

        if (buttonId === "toggle-location-button") {
            const updatedLocation = {
                ...currentSelectedLocation,
                active: !currentSelectedLocation.active,
            };

            toggleAdminLocationActiveStatus(updatedLocation)
                .unwrap()
                .then(() => {
                    logEvent(
                        currentSelectedLocation.active
                            ? "PayrollAdminLocationDisabled"
                            : "PayrollAdminLocationEnabled",
                        null
                    );
                    setSelectedRows([]);
                    showResult(
                        currentSelectedLocation.active
                            ? "Admin Location Disabled"
                            : "Admin Location Enabled",
                        "success"
                    );
                })
                .catch((error) => {
                    console.error("Error toggling admin location:", error);
                    showResult("Failed to update Admin Location", "error");
                });
        }
    };

    return (
        <Grid container spacing={0} alignItems="stretch" sx={{ justify: "space-between", flexGrow: 1 }}>
            <AddEditAdminLocationDrawer
                open={drawerOpen}
                mode={drawerMode}
                location={selectedLocation}
                onClose={() => {
                    setDrawerOpen(false);
                    setSelectedLocation(null);
                }}
                onSaveResult={showResult}
            />
            <CclAlertSnackbar
                open={showSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setShowSnackbar(false)}
            />
            <Grid
                item
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: 1,
                    height: "calc(100vh - 220px)",
                    minHeight: 400,
                }}
            >
                <DataGridPremium
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    getRowId={(row) => row.locCode}
                    checkboxSelection
                    disableSelectionOnClick
                    onSelectionModelChange={(selection) =>
                        setSelectedRows(normalizeSelectionIds(selection))
                    }
                    components={{ Toolbar: CclDataGridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showSaveState: false,
                            hideSettingsButtons: true,
                            savestate: null,
                            clearstate: null,
                            anySelected: isExactlyOneSelected,
                            miscButtons: [
                                {
                                    id: "add-location-button",
                                    caption: "Add Admin. Location",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                },
                                {
                                    id: "edit-location-button",
                                    caption: "Edit Admin. Location",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                    disable: !isExactlyOneSelected,
                                },
                                {
                                    id: "toggle-location-button",
                                    caption: currentSelectedLocation?.active
                                        ? "Disable Admin. Location"
                                        : "Enable Admin. Location",
                                    onClick: null,
                                    returnSelected: false,
                                    restricted: false,
                                    disable: !isExactlyOneSelected,
                                },
                            ],
                            btnClick: handleToolbarButtonClick,
                        },
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default AdminLocationPanel;
